// InternshipForm.jsx
import React, { useState, useEffect } from "react";

const API_URL = "https://api.seengineering-tn.com/api"; // Changez selon votre configuration

// Icônes SVG simples (remplacez par lucide-react si installé)
const Calendar = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const FileText = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const AlertCircle = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckCircle = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const XCircle = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const InternshipForm = () => {
  const [internships, setInternships] = useState([]);
  const [config, setConfig] = useState(null);
  const [isFormActive, setIsFormActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    tel: "",
    etablissement: "",
    diplome: "",
    specialite: "",
    cv: null,
    premierChoix: "",
    deuxiemeChoix: "",
    annee: new Date().getFullYear(),
  });

  useEffect(() => {
    loadInternships();
    checkFormActive();
  }, []);

  const checkFormActive = async () => {
    try {
      const response = await fetch(`${API_URL}/config/active`);
      const data = await response.json();
      setIsFormActive(data.active);
      if (data.config) {
        setConfig(data.config);
        setFormData((prev) => ({ ...prev, annee: data.config.year }));
      }
    } catch (err) {
      console.error("Erreur lors de la vérification du formulaire:", err);
    }
  };

  const loadInternships = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/internships`);
      const data = await response.json();
      setInternships(data);
    } catch (err) {
      setError("Erreur lors du chargement des stages");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setFormData({ ...formData, cv: file });
      setError("");
    } else {
      setError("Veuillez sélectionner un fichier PDF");
      e.target.value = "";
    }
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!isFormActive) {
      setError("Le formulaire n'est pas accessible actuellement");
      return;
    }

    // Validation
    if (
      !formData.prenom ||
      !formData.nom ||
      !formData.email ||
      !formData.tel ||
      !formData.etablissement ||
      !formData.diplome ||
      !formData.specialite ||
      !formData.premierChoix ||
      !formData.deuxiemeChoix
    ) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (!formData.cv) {
      setError("Veuillez joindre votre CV en PDF");
      return;
    }

    if (formData.premierChoix === formData.deuxiemeChoix) {
      setError("Les deux choix doivent être différents");
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Veuillez entrer un email valide");
      return;
    }

    // Validation CGU
    if (!formData.acceptCGU) {
      setError(
        "Vous devez accepter les conditions générales d'utilisation pour continuer"
      );
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("prenom", formData.prenom);
      formDataToSend.append("nom", formData.nom);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("tel", formData.tel);
      formDataToSend.append("etablissement", formData.etablissement);
      formDataToSend.append("diplome", formData.diplome);
      formDataToSend.append("specialite", formData.specialite);
      formDataToSend.append("cv", formData.cv);
      formDataToSend.append("premierChoix", formData.premierChoix);
      formDataToSend.append("deuxiemeChoix", formData.deuxiemeChoix);
      formDataToSend.append("annee", formData.annee);

      const response = await fetch(`${API_URL}/applications`, {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur lors de la soumission");
      }

      setSuccess(
        "Votre candidature a été soumise avec succès ! Vous recevrez une réponse par email."
      );

      // Réinitialiser le formulaire
      setFormData({
        prenom: "",
        nom: "",
        email: "",
        tel: "",
        etablissement: "",
        diplome: "",
        specialite: "",
        cv: null,
        premierChoix: "",
        deuxiemeChoix: "",
        annee: config?.year,
      });

      // Réinitialiser l'input file
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";

      // Scroll to top pour voir le message de succès
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // Vérifier si c'est une erreur d'email déjà existant
      if (
        err.message.includes("email existe déjà") ||
        err.message.includes("EMAIL_ALREADY_EXISTS")
      ) {
        setError(
          "⚠️ Cet email a déjà été utilisé pour une candidature. Une seule candidature par email est autorisée. Si vous pensez qu'il s'agit d'une erreur, veuillez contacter l'administration."
        );
      } else {
        setError(err.message);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-indigo-600 text-white p-8">
            <h1 className="text-3xl font-bold mb-2">Candidature de Stage</h1>
            <p className="text-indigo-100">
              SE Engineering - {config?.year || new Date().getFullYear()}
            </p>
            {config && (
              <div className="mt-4 flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                <span>
                  Période :{" "}
                  {new Date(config.startDate).toLocaleDateString("fr-FR")} -{" "}
                  {new Date(config.endDate).toLocaleDateString("fr-FR")}
                </span>
              </div>
            )}
          </div>

          {/* Messages */}
          {error && (
            <div className="mx-8 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mx-8 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          {/* Formulaire fermé */}
          {!isFormActive ? (
            <div className="p-8 text-center">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Formulaire non disponible
              </h2>
              <p className="text-gray-600">
                Le formulaire n'est pas accessible pour le moment.
              </p>
              {config && (
                <p className="text-sm text-gray-500 mt-2">
                  Période d'ouverture :{" "}
                  {new Date(config.startDate).toLocaleDateString("fr-FR")} -{" "}
                  {new Date(config.endDate).toLocaleDateString("fr-FR")}
                </p>
              )}
            </div>
          ) : (
            /* Formulaire actif */
            <div className="p-8 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Prénom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) =>
                      setFormData({ ...formData, prenom: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Votre prénom"
                  />
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) =>
                      setFormData({ ...formData, nom: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Votre nom"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="votre.email@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Une seule candidature par email est autorisée
                  </p>
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.tel}
                    onChange={(e) =>
                      setFormData({ ...formData, tel: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="+216 XX XXX XXX"
                  />
                </div>

                {/* Établissement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Établissement <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.etablissement}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        etablissement: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="ESPRIT, INSAT, etc."
                  />
                </div>

                {/* Diplôme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de Diplôme <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.diplome}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        diplome: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez Type de Diplôme</option>
                    <option value="Licence">Licence</option>
                    <option value="Master">Master</option>
                    <option value="Ingénieur">Ingénieur</option>
                  </select>
                </div>

                {/* Spécialité */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Spécialité <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.specialite}
                    onChange={(e) =>
                      setFormData({ ...formData, specialite: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Génie Logiciel, Réseaux, etc."
                  />
                </div>

                {/* Année */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Année <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.annee}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        annee: parseInt(e.target.value),
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div> */}
              </div>

              {/* CV */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CV (PDF uniquement) <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {formData.cv && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {formData.cv.name}
                  </p>
                )}
              </div>

              {/* Choix de stages */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Premier choix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Premier Choix <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.premierChoix}
                    onChange={(e) =>
                      setFormData({ ...formData, premierChoix: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un stage</option>
                    {internships.map((int) => (
                      <option key={int._id} value={int._id}>
                        {int.title} ({int.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Deuxième choix */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deuxième Choix <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.deuxiemeChoix}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        deuxiemeChoix: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un stage</option>
                    {internships
                      .filter((int) => int._id !== formData.premierChoix)
                      .map((int) => (
                        <option key={int._id} value={int._id}>
                          {int.title} ({int.type})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Case à cocher CGU */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="acceptCGU"
                    checked={formData.acceptCGU}
                    onChange={(e) =>
                      setFormData({ ...formData, acceptCGU: e.target.checked })
                    }
                    className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="acceptCGU"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    J'ai lu et déclare accepter les{" "}
                    <a
                      href="/internship/CGU"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Conditions Générales d'Utilisation
                    </a>{" "}
                    <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>

              {/* Bouton submit */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    Soumettre ma candidature
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                En soumettant ce formulaire, vous acceptez que vos données
                soient traitées dans le cadre de votre candidature.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternshipForm;
